import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ShoppingCart, Trash2 } from 'lucide-react';

const TREE_PRICE = 8; // €8 per tree
const CO2_PER_TREE = 0.03; // 1 tree = 0.03 ton CO2
const AREA_PER_TREE = 1.5; // 1 tree = 1.5 m²
const VISITOR_PER_TREE = 1000; // 1000 ziyaretçi = 1 ağaç

const TreeCalculator = () => {
  const { t, currentLanguage } = useLanguage();
  const [mode, setMode] = useState('slider');
  const [treeCount, setTreeCount] = useState(0);
  const [co2, setCo2] = useState(0);
  const [area, setArea] = useState(0);
  const [visitors, setVisitors] = useState(0);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);

  // Handle slider or CO2 input
  const handleSlider = (val) => {
    setTreeCount(val);
    setCo2(val * CO2_PER_TREE);
    setArea(val * AREA_PER_TREE);
  };

  const handleCO2Input = (val) => {
    const trees = Math.round(val / CO2_PER_TREE);
    setTreeCount(trees);
    setCo2(trees * CO2_PER_TREE);
    setArea(trees * AREA_PER_TREE);
  };

  const handleVisitorInput = (val) => {
    setVisitors(val);
    const trees = Math.ceil(val / VISITOR_PER_TREE);
    setTreeCount(trees);
    setCo2(trees * CO2_PER_TREE);
    setArea(trees * AREA_PER_TREE);
  };

  const addToCart = () => {
    if (treeCount > 0) {
      const cartItem = {
        id: Date.now(),
        trees: treeCount,
        co2: co2,
        area: area,
        price: treeCount * TREE_PRICE,
        timestamp: new Date().toLocaleString()
      };
      setCart([...cart, cartItem]);
      setShowCart(true);
    }
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  const getTotalTrees = () => {
    return cart.reduce((total, item) => total + item.trees, 0);
  };

  return (
    <div className="bg-neutral-50 rounded-2xl shadow-lg p-8 max-w-4xl mx-auto my-12">
      <h2 className="text-2xl md:text-3xl font-bold text-center text-green-800 mb-6">
        {t('tree-calculator-title')}
      </h2>
      
      {/* Cart Summary */}
      {cart.length > 0 && (
        <div className="bg-white rounded-xl p-4 mb-6 border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart className="text-green-600" size={24} />
              <span className="font-semibold text-green-800">
                {getTotalTrees()} {t('tree-calculator-trees')} - €{getTotalPrice().toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}
              </span>
            </div>
            <button
              onClick={() => setShowCart(!showCart)}
              className="text-green-600 hover:text-green-700 font-medium"
            >
              {showCart ? t('cart-hide') : t('cart-show')}
            </button>
          </div>
        </div>
      )}

      {/* Cart Items */}
      {showCart && cart.length > 0 && (
        <div className="bg-white rounded-xl p-6 mb-6 border border-green-200">
          <h3 className="text-lg font-semibold text-green-800 mb-4">{t('cart-items')}</h3>
          <div className="space-y-3">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className="text-green-600 font-semibold">{item.trees} {t('tree-calculator-trees')}</span>
                  <span className="text-sm text-neutral-600">CO₂: {item.co2.toFixed(3)}t</span>
                  <span className="text-sm text-neutral-600">{item.timestamp}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-green-800">€{item.price.toFixed(2)}</span>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-200">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-green-800">{t('cart-total')}</span>
              <span className="text-xl font-bold text-green-800">€{getTotalPrice().toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
            </div>
            <button
              onClick={() => window.location.href = `/${currentLanguage}/checkout?trees=${getTotalTrees()}&price=${getTotalPrice()}`}
              className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200"
            >
              {t('proceed-to-checkout')}
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-center gap-3 mb-6 flex-wrap">
        <button
          className={`px-5 py-2 rounded-full border font-medium transition-all duration-200 ${mode === 'slider' ? 'bg-green-700 text-white border-green-700' : 'bg-white text-green-700 border-green-700 hover:bg-green-50'}`}
          onClick={() => setMode('slider')}
        >
          {t('tree-calculator-slider')}
        </button>
        <button
          className={`px-5 py-2 rounded-full border font-medium transition-all duration-200 ${mode === 'co2' ? 'bg-green-700 text-white border-green-700' : 'bg-white text-green-700 border-green-700 hover:bg-green-50'}`}
          onClick={() => setMode('co2')}
        >
          {t('tree-calculator-co2')}
        </button>
        <button
          className={`px-5 py-2 rounded-full border font-medium transition-all duration-200 ${mode === 'visitor' ? 'bg-green-700 text-white border-green-700' : 'bg-white text-green-700 border-green-700 hover:bg-green-50'}`}
          onClick={() => setMode('visitor')}
        >
          {t('tree-calculator-visitor')}
        </button>
        <button
          className={`px-5 py-2 rounded-full border font-medium transition-all duration-200 ${mode === 'sample' ? 'bg-green-700 text-white border-green-700' : 'bg-white text-green-700 border-green-700 hover:bg-green-50'}`}
          onClick={() => setMode('sample')}
        >
          {t('tree-calculator-samples')}
        </button>
      </div>
      {/* Slider or CO2 input or Visitor input */}
      {mode === 'slider' && (
        <div className="flex flex-col items-center mb-8">
          <input
            type="range"
            min={0}
            max={100}
            value={treeCount}
            onChange={e => handleSlider(Number(e.target.value))}
            className="w-full accent-green-700 h-2 rounded-lg appearance-none bg-neutral-200 mb-2"
          />
          <span className="text-green-800 font-semibold">{treeCount} {t('tree-calculator-trees')}</span>
        </div>
      )}
      {mode === 'co2' && (
        <div className="flex flex-col items-center mb-8">
          <input
            type="number"
            min={0}
            step={0.01}
            value={co2}
            onChange={e => handleCO2Input(Number(e.target.value))}
            className="w-40 px-4 py-2 border border-green-300 rounded-lg text-center text-green-800 font-semibold mb-2"
            placeholder={t('tree-calculator-co2-placeholder')}
          />
          <span className="text-green-800 font-semibold">{treeCount} {t('tree-calculator-trees')}</span>
        </div>
      )}
      {mode === 'visitor' && (
        <div className="flex flex-col items-center mb-8">
          <input
            type="number"
            min={0}
            step={100}
            value={visitors}
            onChange={e => handleVisitorInput(Number(e.target.value))}
            className="w-48 px-4 py-2 border border-green-300 rounded-lg text-center text-green-800 font-semibold mb-2"
            placeholder={t('tree-calculator-visitor-placeholder')}
          />
          <span className="text-green-800 font-semibold">{treeCount} {t('tree-calculator-trees')}</span>
          <span className="text-green-700 text-xs mt-1">{t('tree-calculator-visitor-note')}</span>
        </div>
      )}
      {mode === 'sample' && (
        <div className="flex flex-col items-center mb-8 text-green-800">
          <div className="mb-2">{t('tree-calculator-sample-1')}</div>
          <div className="mb-2">{t('tree-calculator-sample-2')}</div>
          <div className="mb-2">{t('tree-calculator-sample-3')}</div>
        </div>
      )}
      {/* Results */}
      <div className="bg-white rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
        <div className="flex items-center gap-4">
          <span className="inline-block">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g>
                <ellipse cx="28" cy="28" rx="28" ry="28" fill="#E6F4EA"/>
                <ellipse cx="28" cy="34" rx="16" ry="10" fill="#A7E9AF"/>
                <ellipse cx="18" cy="28" rx="8" ry="12" fill="#4CAF50"/>
                <ellipse cx="38" cy="28" rx="8" ry="12" fill="#81C784"/>
                <ellipse cx="28" cy="24" rx="6" ry="10" fill="#388E3C"/>
              </g>
            </svg>
          </span>
          <span className="text-3xl font-bold text-green-800">{treeCount}</span>
          <span className="text-xl text-green-800 ml-1">{t('tree-calculator-trees')}</span>
        </div>
        <div className="flex flex-col md:flex-row gap-4 mt-4 md:mt-0">
          <div className="bg-neutral-100 rounded-lg px-6 py-3 flex items-center gap-2">
            <span className="text-green-800 font-bold">◆ {area.toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})} m²</span>
          </div>
          <div className="bg-neutral-100 rounded-lg px-6 py-3 flex items-center gap-2">
            <span className="text-green-800 font-bold">CO₂ {co2.toLocaleString('tr-TR', {minimumFractionDigits:3, maximumFractionDigits:3})}t</span>
          </div>
        </div>
      </div>
      {/* Price and CTA */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <div className="text-2xl font-bold text-green-800 mb-1">€{(treeCount * TREE_PRICE).toLocaleString('tr-TR', {minimumFractionDigits:2, maximumFractionDigits:2})}</div>
          <div className="text-green-700 text-sm">{t('tree-calculator-unit-price')}</div>
        </div>
        <button 
          onClick={addToCart}
          disabled={treeCount === 0}
          className="bg-green-500 hover:bg-green-600 disabled:bg-neutral-400 disabled:cursor-not-allowed text-white font-bold px-8 py-4 rounded-full text-lg transition-all duration-200 shadow-lg"
        >
          {t('add-to-cart')}
        </button>
      </div>
    </div>
  );
};

export default TreeCalculator; 